import { apiUrl } from "@/config/api";

export type AuthOtpJson = Record<string, unknown>;

export async function postAuthGetOtp(body: {
  mobileNumber: string;
  language_code: string;
}): Promise<{ ok: boolean; status: number; json: AuthOtpJson }> {
  const res = await fetch(apiUrl("/auth/get-otp"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
  });
  const json = (await res.json().catch(() => ({}))) as AuthOtpJson;
  const success = json.success !== false && res.ok;
  return { ok: success, status: res.status, json };
}

export async function postAuthVerifyOtp(body: {
  mobileNumber: string;
  otp: string;
}): Promise<{ ok: boolean; status: number; json: AuthOtpJson }> {
  const res = await fetch(apiUrl("/auth/verify-otp"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    cache: "no-store",
  });
  const json = (await res.json().catch(() => ({}))) as AuthOtpJson;
  const success = json.success !== false && res.ok;
  return { ok: success, status: res.status, json };
}

function pickString(
  o: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

export type ParsedVerifyOtpFields = {
  customerId?: string;
  tenantId?: string;
  businessUnitId?: string;
  qrCodeUrl?: string;
  name?: string;
  email?: string;
};

/**
 * Maps `verify-otp` `result` (or top-level fallbacks) into session fields.
 */
function mergePick(
  a: Record<string, unknown>,
  b: Record<string, unknown>
): ParsedVerifyOtpFields {
  const customerId =
    pickString(a, "customer_id", "customerId", "uuid", "customer_uuid") ??
    pickString(b, "customer_id", "customerId", "uuid", "customer_uuid");
  const tenantId =
    pickString(a, "tenant_id", "tenantId") ??
    pickString(b, "tenant_id", "tenantId");
  const businessUnitId =
    pickString(
      a,
      "business_unit_id",
      "businessUnitId",
      "business_unit_ID"
    ) ??
    pickString(b, "business_unit_id", "businessUnitId", "business_unit_ID");
  const qrCodeUrl =
    pickString(a, "qr_code_url", "qrCodeUrl", "qrcode_url") ??
    pickString(b, "qr_code_url", "qrCodeUrl", "qrcode_url");
  const firstName =
    pickString(a, "first_name", "firstName", "given_name", "givenName") ??
    pickString(b, "first_name", "firstName", "given_name", "givenName");
  const lastName =
    pickString(a, "last_name", "lastName", "family_name", "familyName") ??
    pickString(b, "last_name", "lastName", "family_name", "familyName");
  const fullName =
    pickString(a, "name", "full_name", "fullName", "customer_name", "customerName") ??
    pickString(b, "name", "full_name", "fullName", "customer_name", "customerName");
  const combinedName = [firstName, lastName]
    .filter((s): s is string => !!s && s.length > 0)
    .join(" ")
    .trim();
  const name = combinedName || fullName;
  const email =
    pickString(a, "email", "email_address", "emailAddress", "Email") ??
    pickString(b, "email", "email_address", "emailAddress", "Email");
  return {
    ...(customerId ? { customerId } : {}),
    ...(tenantId ? { tenantId } : {}),
    ...(businessUnitId ? { businessUnitId } : {}),
    ...(qrCodeUrl ? { qrCodeUrl } : {}),
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
  };
}

export function parseVerifyOtpResult(
  json: AuthOtpJson
): ParsedVerifyOtpFields {
  let root: Record<string, unknown>;
  const result = json.result;
  if (result && typeof result === "object" && !Array.isArray(result)) {
    root = result as Record<string, unknown>;
  } else if (
    json.data &&
    typeof json.data === "object" &&
    !Array.isArray(json.data)
  ) {
    root = json.data as Record<string, unknown>;
  } else {
    root = json;
  }

  const nested =
    root.customer && typeof root.customer === "object" && !Array.isArray(root.customer)
      ? (root.customer as Record<string, unknown>)
      : root.user && typeof root.user === "object" && !Array.isArray(root.user)
        ? (root.user as Record<string, unknown>)
        : null;

  const dataLayer =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? (root.data as Record<string, unknown>)
      : null;

  if (nested && dataLayer) {
    return mergePick(mergePick(root, dataLayer), nested);
  }
  if (nested) {
    return mergePick(root, nested);
  }
  if (dataLayer) {
    return mergePick(root, dataLayer);
  }

  return mergePick(root, {});
}

export function messageFromAuthJson(json: AuthOtpJson): string {
  const m = json.message;
  return typeof m === "string" && m.trim() ? m.trim() : "Request failed";
}
